import { HttpResponse } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { defer, Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { ApiService } from './api.service';
import { SESSION_POLICY_PATH, SESSION_POLICY_READ_TIMEOUT_MS, SessionActivityService } from './session-activity.service';
import { classifyEnforcement, SESSION_POLICY_FIELDS, SessionPolicyService } from './session-policy.service';
import { UserService } from './user.service';

export { SESSION_POLICY_PATH, SESSION_POLICY_READ_TIMEOUT_MS };

/** What one policy read did (diagnostics and tests; callers do not branch on it). */
export type SessionPolicyReadOutcome =
  | 'skipped-no-token'
  | 'skipped-expired-token'
  | 'skipped-no-tenant-claim'
  | 'skipped-session-ended'
  | 'skipped-policy-known'
  | 'skipped-token-not-selected-tenant'
  | 'skipped-no-verifier'
  | 'skipped-in-flight'
  | 'applied'
  | 'off'
  | 'no-session'
  | 'unavailable'
  | 'discarded-stale'
  | 'discarded-tenant-mismatch'
  | 'discarded-incomplete'
  | 'discarded-renewal-won'
  | 'failed';

/**
 * D9 (session-contract-v3): the passive policy read, `POST {API_BASE_URL}/token/session-policy` with an empty body
 * (the same signed-empty-body convention as the heartbeat: `ApiService.postEmpty`, bearer + `X-Payload-HMAC`, bounded by
 * {@link SESSION_POLICY_READ_TIMEOUT_MS}). The server never counts it as activity and never extends anything.
 *
 * Invoked (a) at app start when a session token exists and no server policy is known for the current generation,
 * (b) when UserService stored a token whose response carried no policy block, and (c) after a company switch (the policy of
 * the new tenant must be read AFTER the new token is stored). Each tab reads for itself; no BroadcastChannel is used.
 *
 * APPLY RULES (all must hold when the response lands; otherwise it is discarded and nothing changes):
 *  1. the policy generation is unchanged since the read left (logout, 401, tenant switch, brand-new session bump it);
 *  2. the session has not ended and a token is still held;
 *  3. the response `customer_id` is present and equals the `rt.cid` of the token used to send AND of the token held now
 *     (a mismatch is discarded, NOT treated as a switch; an omitted `customer_id` is an incomplete answer, C9);
 *  4. no server policy was applied meanwhile for this generation (a renewal that landed first wins).
 * A failed / timed-out / malformed read never downgrades an enforced policy and invents no fallback (the mode only becomes
 * `unavailable` when nothing was ever applied); a 401 follows the existing interceptor path (global logout). No automatic retry.
 */
@Injectable({ providedIn: 'root' })
export class SessionPolicyReadService {
  private readonly injector = inject(Injector);
  private readonly policy = inject(SessionPolicyService);
  private readonly activity = inject(SessionActivityService);
  private started = false;
  /** Generation of the read currently in flight (one per generation). */
  private inFlightGeneration: number | null = null;

  /**
   * Wires the request channel and performs the startup read. Idempotent. Call once from the app shell (after the services
   * it reads exist); a stored session token then never leaves the client waiting on the local default alone.
   */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.policy.readRequested$.subscribe(() => this.readIfNeeded());
    this.readIfNeeded();
  }

  /** Fire-and-forget {@link request}. */
  readIfNeeded(): void {
    this.request().subscribe();
  }

  /**
   * Reads the policy when it is needed. Emits exactly one {@link SessionPolicyReadOutcome} and completes; never errors.
   * @returns The outcome of this attempt.
   */
  request(): Observable<SessionPolicyReadOutcome> {
    return defer(() => {
      const user = this.injector.get(UserService);
      if (this.activity.isEnded) {
        return of<SessionPolicyReadOutcome>('skipped-session-ended');
      }
      if (!user.hasSessionToken()) {
        return of<SessionPolicyReadOutcome>('skipped-no-token');
      }
      if (!user.hasUnexpiredSessionToken()) {
        // The existing renewal path mints a new token (whose policy block, or the read that follows, applies the policy).
        return of<SessionPolicyReadOutcome>('skipped-expired-token');
      }
      const tenantAtSend = user.sessionTenantId();
      if (tenantAtSend === null) {
        return of<SessionPolicyReadOutcome>('skipped-no-tenant-claim'); // cannot bind a policy to a tenant (legacy token)
      }
      if (this.policy.hasServerPolicy && !this.policy.refreshWanted) {
        return of<SessionPolicyReadOutcome>('skipped-policy-known');
      }
      const selected = this.policy.selectedTenantId;
      if (selected !== null && selected !== tenantAtSend) {
        // NEW-5/NEW-7: the company selected in the last switch has no token yet (SSO keeps the old token). The server would
        // answer for the token's tenant, which must never be applied to the selected one, so no call is made.
        return of<SessionPolicyReadOutcome>('skipped-token-not-selected-tenant');
      }
      if (!user.getVerifier()) {
        return of<SessionPolicyReadOutcome>('skipped-no-verifier'); // the signed-empty-body HMAC needs the verifier; UserService re-requests once it is stored
      }
      const generationAtSend = this.policy.generation;
      if (this.inFlightGeneration === generationAtSend) {
        return of<SessionPolicyReadOutcome>('skipped-in-flight');
      }
      this.inFlightGeneration = generationAtSend;
      // Fix3: a refresh read (failed-switch fallback, measurement after a renewal) may land over an already known policy.
      const refresh = this.policy.consumeRefresh();
      const ticket = this.policy.readStarted();
      // Every exit path (synchronous throw while building the request, error, timeout, completion, unsubscribe, reset) must
      // release the hold exactly once; otherwise the local-default logout timer could stay suspended for the page lifetime.
      let released = false;
      const release = (): void => {
        if (released) {
          return;
        }
        released = true;
        if (this.inFlightGeneration === generationAtSend) {
          this.inFlightGeneration = null;
        }
        this.policy.readFinished(ticket);
        // Fix4 item 2: a refresh read that was asked for while THIS read was in flight ('skipped-in-flight' left it pending, the
        // generation is unchanged) runs now, exactly once: the follow-up read consumes the request (consumeRefresh), so it cannot
        // loop, and a reset/logout/tenant change since has already cleared it (refreshWanted is generation-scoped).
        if (this.policy.refreshWanted) {
          this.readIfNeeded();
        }
      };
      const onFailure = (err: unknown): Observable<SessionPolicyReadOutcome> => {
        // 401: the session-status interceptor already ran the global logout. Anything else (503, 408 timeout, network):
        // no information. An applied policy is never downgraded and no fallback is invented.
        if (this.policy.generation === generationAtSend) {
          this.policy.noteUnavailable();
        }
        // Fix3 item 5: one warning per distinct status per session (the 'unavailable' state and the 'failed' outcome are unaffected).
        const status = (err as { status?: number })?.status ?? '';
        this.policy.noteDiagnosticOnce(`read-failed:${status}`, `[SessionPolicyRead] policy read failed; no policy applied. ${status}`.trimEnd());
        return of<SessionPolicyReadOutcome>('failed');
      };
      try {
        const sentMono = monotonicNow();
        const url = `${environment.API_BASE_URL}${SESSION_POLICY_PATH}`;
        return this.injector.get(ApiService).postEmpty<unknown>(url, { timeoutMs: SESSION_POLICY_READ_TIMEOUT_MS }).pipe(
          map((res): SessionPolicyReadOutcome => {
            const receivedAtMs = Date.now();
            const sentAtMs = receivedAtMs - Math.max(0, monotonicNow() - sentMono);
            return this.apply(res, user, generationAtSend, tenantAtSend, sentAtMs, receivedAtMs, refresh);
          }),
          catchError(onFailure),
          finalize(release),
        );
      } catch (err: unknown) {
        release();
        return onFailure(err);
      }
    });
  }

  /** Validates and applies one response (see the class comment for the rules). */
  private apply(res: HttpResponse<unknown>, user: UserService, generationAtSend: number, tenantAtSend: number, sentAtMs: number, receivedAtMs: number, refresh = false): SessionPolicyReadOutcome {
    if (this.policy.generation !== generationAtSend || this.activity.isEnded || !user.hasSessionToken()) {
      return 'discarded-stale'; // logout, 401, token replacement for another session, or a tenant switch since send
    }
    const body = res.body as { success?: unknown; result?: unknown } | null;
    const result = body?.result;
    if (res.status !== 200 || !body || body.success !== true || typeof result !== 'object' || result === null) {
      this.policy.noteUnavailable();
      return 'failed';
    }
    const raw = result as Record<string, unknown>;
    const field = classifyEnforcement(raw);
    if (field.kind === 'no_session') {
      return 'no-session'; // a token without a server session: no policy information (never Off)
    }
    if (field.kind === 'unavailable') {
      this.policy.noteUnavailable();
      return 'unavailable';
    }
    if (this.policy.hasServerPolicy && !refresh) {
      return 'discarded-renewal-won'; // a renewal (or an earlier read) applied a policy for this generation first
    }
    const tenantNow = user.sessionTenantId();
    if (tenantNow === null || tenantNow !== tenantAtSend || (this.policy.selectedTenantId !== null && this.policy.selectedTenantId !== tenantNow)) {
      return 'discarded-tenant-mismatch'; // the token was replaced by another tenant's (or is not the selected one): stale, not a switch
    }
    if (field.kind === 'off') {
      // Off bodies carry no customer_id (contract section 3); the bearer's tenant is the one the call was made for.
      this.policy.applyFromResponse(raw, tenantNow, { sentAtMs, receivedAtMs });
      return 'off';
    }
    const bodyTenant = raw[SESSION_POLICY_FIELDS.customerId];
    if (typeof bodyTenant !== 'number' || !Number.isInteger(bodyTenant)) {
      return 'discarded-incomplete'; // C9: omitted customer_id = incomplete answer
    }
    if (bodyTenant !== tenantNow) {
      return 'discarded-tenant-mismatch'; // never applied and never treated as a switch
    }
    const outcome = this.policy.applyFromResponse(raw, tenantNow, { sentAtMs, receivedAtMs });
    if (outcome !== 'applied') {
      return outcome === 'tenant-mismatch' ? 'discarded-tenant-mismatch' : 'failed';
    }
    // C4: a session-policy result is a server measurement of the idle time left (this call is never counted activity).
    // logout = receipt time + remaining, warning = send time + remaining - warning. The applyFromResponse above may have made
    // AuthenticationService seed the estimate with an unmeasured placeholder (seedOnly); this REAL measurement replaces that
    // seed outright (SessionActivityService.mergeServerBounds), while against an earlier real measurement it is max-wins.
    const remaining = raw[SESSION_POLICY_FIELDS.idleRemainingSeconds];
    if (this.policy.enforced && typeof remaining === 'number' && Number.isFinite(remaining) && remaining >= 0) {
      this.activity.syncFromRemaining(sentAtMs, this.policy.effectiveIdleMs, Math.round(remaining * 1000), receivedAtMs);
    }
    return 'applied';
  }
}

/** Monotonic milliseconds for round-trip measurement (wall-clock fallback where `performance` is unavailable). */
function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}
